import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export interface MemberData {
  id: number;
  full_name: string;
  email: string;
  whatsapp: string;
  country_code: string;
  city_state: string;
  profession: string;
  photo: string | null;
  status: string;
}

interface UseMemberResult {
  member: MemberData | null;
  loading: boolean;
  refresh: () => void;
}

async function fetchMyMember(): Promise<MemberData | null> {
  try {
    const { data: found, error } = await supabase.rpc("my_member_profile");
    if (error || !found) return null;
    return {
      id: found.id,
      full_name: found.full_name || "",
      email: found.email || "",
      whatsapp: found.whatsapp || "",
      country_code: found.country_code || "",
      city_state: found.city_state || "",
      profession: found.profession || "",
      photo: found.photo,
      status: found.status || "",
    };
  } catch {
    return null;
  }
}

export default function useMember(): UseMemberResult {
  const [member, setMember] = useState<MemberData | null>(null);
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setLoading(false);

      if (session?.user?.email) {
        const found = await fetchMyMember();
        setMember(found);
      } else {
        setMember(null);
      }
    } catch {
      setMember(null);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    checkSession().then(() => {
      // initial check done
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (cancelled) return;
        // Callback NAO pode ser async — usamos .then() pra evitar deadlock no SDK
        if (session?.user?.email) {
          fetchMyMember().then((found) => {
            if (!cancelled) setMember(found);
          });
        } else {
          setMember(null);
        }
      }
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [checkSession]);

  return { member, loading, refresh: checkSession };
}